import { useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useKeyboard } from '../hooks/useKeyboard';
import styles from './Layout.module.css';

export function Layout() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  // Auto-scroll the active nav link into view on mobile
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>(`.${styles.navLinkActive}`);
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [location.pathname]);

  const { showHelp, setShowHelp } = useKeyboard({
    onSearch: () => {
      const searchInput = document.querySelector<HTMLInputElement>('[data-search-input]');
      searchInput?.focus();
    },
    onEscape: () => setShowHelp(false),
  });

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <div className={styles.logo}>
          <svg className={styles.logoIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18M3 9h6M3 15h6" />
          </svg>
          <span className={styles.logoText}>Admin Panel</span>
        </div>

        <nav ref={navRef} className={styles.nav}>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
            }
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/organizations"
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
            }
          >
            Organizations
          </NavLink>
          <NavLink
            to="/waitlist"
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
            }
          >
            Waitlist
          </NavLink>
        </nav>

        <div className={styles.actions}>
          <button
            className={styles.searchButton}
            onClick={() => navigate('/organizations')}
          >
            <span>Search...</span>
            <kbd className={styles.searchKbd}>
              <span>Cmd</span>
              <span>K</span>
            </kbd>
          </button>
          <button
            className={styles.themeToggle}
            onClick={toggleTheme}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5" />
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      {showHelp && (
        <>
          <div className={styles.helpOverlay} onClick={() => setShowHelp(false)} />
          <div className={styles.helpModal}>
            <h2 className={styles.helpTitle}>Keyboard Shortcuts</h2>

            <div className={styles.helpSection}>
              <h3 className={styles.helpSectionTitle}>Global</h3>
              <div className={styles.helpList}>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>Cmd+K</kbd>
                  <span className={styles.helpDesc}>Open search</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>Esc</kbd>
                  <span className={styles.helpDesc}>Close modal/search</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>?</kbd>
                  <span className={styles.helpDesc}>Show this help</span>
                </div>
              </div>
            </div>

            <div className={styles.helpSection}>
              <h3 className={styles.helpSectionTitle}>List Navigation</h3>
              <div className={styles.helpList}>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>j</kbd>
                  <span className={styles.helpDesc}>Move down</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>k</kbd>
                  <span className={styles.helpDesc}>Move up</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>gg</kbd>
                  <span className={styles.helpDesc}>Jump to top</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>G</kbd>
                  <span className={styles.helpDesc}>Jump to bottom</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>Enter</kbd>
                  <span className={styles.helpDesc}>Open selected</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>/</kbd>
                  <span className={styles.helpDesc}>Focus search</span>
                </div>
              </div>
            </div>

            <div className={styles.helpSection}>
              <h3 className={styles.helpSectionTitle}>Detail View</h3>
              <div className={styles.helpList}>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>h</kbd>
                  <span className={styles.helpDesc}>Go back</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>1-9</kbd>
                  <span className={styles.helpDesc}>Jump to section</span>
                </div>
                <div className={styles.helpItem}>
                  <kbd className={styles.helpKey}>e</kbd>
                  <span className={styles.helpDesc}>Edit overrides</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
